import { useParams, useNavigate, useSearchParams, useLocation } from "react-router";
import React, { useEffect, useState, useRef } from "react";
import { useKeycloak } from "@react-keycloak/web";
import { useProjectActionItemsPage, useProjectActionItemsStore, useProjectDailyUpdatePage, useProjectDailyUpdateStore, useProjectsPage, useProjectsStore } from "../state/projects";
import { useEscalationMatrixPage } from "../state/escalationMatrix";
import { UserInfo } from "../components/UserInfo";
import { createProjectItem, updateProject, createProjectPhase } from "../api/projects";

import { createProjectDailyUpdates } from "../api/dailyUpdates";
import { createProjectActionItems } from "../api/actionItems";
import { toast } from "react-toastify";
import { TabMeetingNotes } from "../components/Project/TabMeetingNotes";

import { ProjectMaterialTracker } from '../components/Project/ProjectMaterialTracker';
import { ProjectTeam } from '../components/Project/ProjectTeam';
import { EscalationRules } from '../components/Project/EscalationRules';
import { TabProjectPlan } from '../components/Project/TabProjectPlan';
import { TabActionItems } from '../components/Project/TabActionItems';
import { TabDailyUpdates } from '../components/Project/TabDailyUpdates';
import { TabDocuments } from '../components/Project/TabDocuments';
import { TabScopeOfWork } from '../components/Project/TabScopeOfWork';
import { TabAssets } from '../components/Project/TabAssets';
import { Warranty } from "./Warranty";
import { CreateDropdown } from "../components/CreateDropdown";
import { AddUserModal } from "../components/Project/AddUserModal";
import { AddProjectItemModal } from "../components/Project/AddProjectItemModal";
import { AddActionItemModal } from "../components/Project/AddActionItemModal";
import { AddDailyUpdateModal } from "../components/Project/AddDailyUpdateModal";
import { LoadingSpinner } from "../components/LoadingSpinner";
import ConfirmationDialog from "../components/ConfirmationDialog";

import { useProjectUsers } from "../hooks/useProjectUsers";
import { ProjectAccessProvider } from "../context/ProjectAccessProvider";
import { NewProjectForm } from "../components/NewProjectForm";
import { NotificationButton } from "../components/NotificationButton";
import { ProjectLocations } from "../components/Project/ProjectLocations";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { ProjectDescription } from "../components/Project/ProjectDescription";
import { ArchivedProjectDetail } from "../components/Project/ArchivedProjectDetail";
import { TabDownloadLogs } from "../components/Project/TabDownloadLogs";
import { getProjectAccess, type ProjectAccess } from "../api/access";
import { canViewDownloadLogs } from "../helpers/projectPermissions";
import { DOWNLOAD_LOGS_ENABLED } from "../config/featureFlags";


interface TabType {
  value: string;
  label: string;
  disabled?: boolean;
  hidden?: boolean;
  devBadge?: boolean; // show DEV badge on prod
}

const TABS: TabType[] = [
  {
    value: 'action-items',
    label: 'Action Items'
  },
  {
    value: 'sow',
    label: 'Scope of Work'
  },
  {
    value: 'project-plan-2',
    label: 'Project Plan'
  },
  {
    value: 'daily-updates',
    label: 'Daily Updates'
  },
  {
    value: 'meeting-notes',
    label: 'Meeting Notes'
  },
  {
    value: 'team',
    label: 'Team'
  },
  {
    value: 'material-tracker',
    label: 'Material Tracker',
    hidden: true
  },
  {
    value: 'assets',
    label: 'Assets'
  },
  {
    value: 'documents',
    label: 'Documents'
  },
  {
    value: 'warranties',
    label: 'Warranties'
  },
  {
    value: 'locations',
    label: 'Locations'
  },
  {
    value: 'settings',
    label: 'Settings'
  },
  {
    value: 'download-logs',
    label: 'Download Log'
  },
];

const ActiveProject = () => {
  // Add cleanup flag to prevent DOM operations after unmount. This must be a
  // ref, not state: under React StrictMode's dev-only mount/cleanup/remount
  // simulation, a state-based flag set `true` by the cleanup never gets reset
  // back to `false` (nothing re-runs the useState initializer), permanently
  // disabling every effect gated on it for the rest of the component's life.
  const isUnmountedRef = useRef(false);
  const params = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { keycloak: { token, tokenParsed } } = useKeycloak();
  const { isFeatureEnabled } = useFeatureFlag();
  const navigate = useNavigate();
  const { loadProjectById, loadProjectsPage } = useProjectsPage();
  const { loadProjectDailyUpdatesPage } = useProjectDailyUpdatePage(params.projectId || '');
  const { loadProjectActionItemsPage } = useProjectActionItemsPage(params.projectId || '');
  const { projectActionItems } = useProjectActionItemsStore();
  const { projectDailyUpdates } = useProjectDailyUpdateStore();
  const { loadEscalationMatrixLevels } = useEscalationMatrixPage(params.projectId || '');


  const [showNewProject, setShowNewProject] = useState<boolean>(false);
  const [showAddTeamMember, setShowAddTeamMember] = useState<boolean>(false);
  const [showAddTask, setShowAddTask] = useState<boolean>(false);
  const [showAddActionItem, setShowAddActionItem] = useState<boolean>(false);
  const [showAddDailyUpdate, setShowAddDailyUpdate] = useState<boolean>(false);
  const isProdEnv = typeof window !== 'undefined' && window.location.hostname.includes('app.finderz.ai');
  
  // Track if project was initially loaded successfully
  const wasProjectLoadedRef = useRef(false);


  
  // Initialize activeTab from URL params, navigation state, localStorage or use default
  const [activeTab, setActiveTab] = useState(() => {
    // Check state from navigation first (from MapPopup)
    const stateTab = location.state?.activeTab;
    if (stateTab && TABS.find(tab => tab.value === stateTab && !tab.hidden)) {
      return stateTab;
    }
    
    // Check URL parameters (for deep linking)
    const urlTab = searchParams.get('tab');
    if (urlTab && TABS.find(tab => tab.value === urlTab && !tab.hidden)) {
      return urlTab;
    }
    
    // Fallback to localStorage
    const savedTab = localStorage.getItem('activeProjectTab');
    // If user was on documents tab (now hidden), redirect to action-items
    if (savedTab === 'documents') {
      return 'action-items';
    }
    // If user was on old project-plan tab, redirect to action-items
    if (savedTab === 'project-plan') {
      return 'action-items';
    }
    // If user was on old action-items-2 tab, redirect to action-items
    if (savedTab === 'action-items-2') {
      return 'action-items';
    }
    // If user was on escalation-matrix tab (now merged with team), redirect to team
    if (savedTab === 'escalation-matrix') {
      return 'team';
    }
    // If user was on old description tab, redirect to settings
    if (savedTab === 'description') {
      return 'settings';
    }
    // If user was on material-tracker tab (now hidden), redirect to assets
    if (savedTab === 'material-tracker') {
      return 'assets';
    }
    
    // Make sure we only return visible tabs
    if (savedTab && TABS.find(tab => tab.value === savedTab && !tab.hidden)) {
      return savedTab;
    }
    
    return 'action-items'; // Default to action-items
  });

  // Extract highlighted item ID from URL parameters
  const highlightedItemId = searchParams.get('item');
  
  const projectData: ProjectInterface | 'not-found' = useProjectsStore((state) => state.fullProjects.find((p) => p.id === params.projectId) || 'not-found');
  const [projectForm, setProjectForm] = useState<{name: string, description: string}>({
    name: typeof projectData === 'string' ? '' : projectData?.name || '',
    description: typeof projectData === 'string' ? '' : projectData?.description || '',
  });

  // Hook for project users (for the global Add Team Member modal)
  const projectId = typeof projectData === 'object' ? projectData.id || '' : '';
  const { users: projectUsers, roles, addUser } = useProjectUsers(projectId);

  // Lightweight, independent access fetch used only to gate the Download Log
  // tab's visibility — this component instantiates ProjectAccessProvider
  // below, so it cannot consume the context it itself provides.
  const [downloadLogsAccess, setDownloadLogsAccess] = useState<ProjectAccess | null>(null);
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    getProjectAccess(projectId)
      .then(a => { if (!cancelled) setDownloadLogsAccess(a); })
      .catch(err => console.error('Failed to load access for Download Log tab gate:', err));
    return () => { cancelled = true; };
  }, [projectId]);
  const isProjectOwner = typeof projectData === 'object' && projectData.owner_id === (tokenParsed as { sub?: string } | undefined)?.sub;





  // Function to get full address from site - same logic as MapPopup
  const getFullAddress = (site: any) => {
    if (!site) return '';
    
    // First, try to use formatted_address from Google Address data
    if (site.google_address_data?.formatted_address) {
      return site.google_address_data.formatted_address;
    }
    
    // Fallback to site.address field
    if (site.address) {
      return site.address;
    }
    
    // Last resort: try to construct from available data
    if (site.google_address_data) {
      const { city, state, locality, administrative_area_level_1 } = site.google_address_data;
      
      // Use new backend fields
      if (city && state) {
        return `${city}, ${state}`;
      }
      if (city) {
        return city;
      }
      
      // Fallback to old fields for backward compatibility  
      if (locality && administrative_area_level_1) {
        return `${locality}, ${administrative_area_level_1}`;
      }
      if (locality) {
        return locality;
      }
    }
    
    return site.name || 'No address specified';
  };

  // Assets refresh counter to force reload after returning from Rack page
  const [assetsRefreshKey, setAssetsRefreshKey] = useState(0);
  
  // Project name edit confirmation
  const [showNameEditConfirmation, setShowNameEditConfirmation] = useState(false);
  const [pendingNameChange, setPendingNameChange] = useState('');

  // Function to change tab with localStorage saving and URL update
  const handleTabChange = (tabValue: string) => {
    const previousTab = activeTab;
    setActiveTab(tabValue);
    localStorage.setItem('activeProjectTab', tabValue);
    
    // Update URL parameter to persist tab selection across page reloads
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', tabValue);
    setSearchParams(newSearchParams, { replace: true });
    
    console.log('📑 Tab changed:', { from: previousTab, to: tabValue, url: `?${newSearchParams.toString()}` });
    
    // Force refresh TabAssets when switching TO assets tab
    if (tabValue === 'assets' && previousTab !== 'assets') {
      setAssetsRefreshKey(prev => prev + 1);
      console.log('🔄 Forcing TabAssets refresh due to tab change');
    }
  };

  // Sync URL parameter with activeTab on mount (if URL doesn't have tab param)
  useEffect(() => {
    const urlTab = searchParams.get('tab');
    if (!urlTab && activeTab) {
      // URL doesn't have tab parameter, but we have activeTab from localStorage or default
      // Add it to URL to ensure consistency
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tab', activeTab);
      setSearchParams(newSearchParams, { replace: true });
      console.log('📑 Initial URL sync: added tab parameter', activeTab);
    }
  }, []); // Run only on mount

  useEffect(() => {
    if (token && !isUnmountedRef.current) {
      loadProjectsPage(1);
      if (params.projectId && projectData === 'not-found') {
        loadProjectById(params.projectId).catch((error: any) => {
          console.error('❌ Failed to load project:', error);
          const errorMessage = error.message || '';
          
          // Handle access errors gracefully - redirect to dashboard
          if (errorMessage.includes('not found') || 
              errorMessage.includes('404')) {
            toast.error('Project not found or has been deleted. Redirecting to dashboard...');
            localStorage.removeItem('activeProjectTab');
            setTimeout(() => {
              navigate('/');
            }, 1500);
          } else if (errorMessage.includes('403') || 
                     errorMessage.includes('Forbidden') ||
                     errorMessage.includes('access denied') ||
                     errorMessage.includes('no access')) {
            toast.error('You no longer have access to this project. Redirecting to dashboard...');
            localStorage.removeItem('activeProjectTab');
            setTimeout(() => {
              navigate('/');
            }, 1500);
          } else if (errorMessage.includes('401') || 
                     errorMessage.includes('Unauthorized')) {
            toast.error('Your session has expired. Please log in again.');
            localStorage.removeItem('activeProjectTab');
            setTimeout(() => {
              navigate('/');
            }, 1500);
          } else {
            // For other errors, also redirect to dashboard but with generic message
            toast.error('Unable to load project. Redirecting to dashboard...');
            localStorage.removeItem('activeProjectTab');
            setTimeout(() => {
              navigate('/');
            }, 2000);
          }
        });
      } else {
        setProjectForm({
          name: typeof projectData === 'string' ? '' : projectData?.name || '',
          description: typeof projectData === 'string' ? '' : projectData?.description || '',
        });
      }
      
      // Automatically load action items and daily updates for all projects
      if (params.projectId) {
        loadProjectActionItemsPage({ page: 1 });
        loadProjectDailyUpdatesPage(1);
      }
    }
  }, [params.projectId, token, projectData]);

  // Cleanup effect to prevent DOM operations after unmount. Reset on setup too,
  // so StrictMode's dev-only mount/cleanup/remount simulation doesn't leave
  // this permanently `true` after the initial mount (see comment at declaration).
  useEffect(() => {
    isUnmountedRef.current = false;
    return () => {
      isUnmountedRef.current = true;
    };
  }, []);

  // Reset project loaded ref when project ID changes (switching between projects)
  useEffect(() => {
    wasProjectLoadedRef.current = false;
  }, [params.projectId]);

  // Track when project is successfully loaded
  useEffect(() => {
    if (projectData && projectData !== 'not-found') {
      wasProjectLoadedRef.current = true;
    }
  }, [projectData]);

  // Watch for project access loss - redirect if project becomes unavailable
  useEffect(() => {
    // Only trigger if we HAD access but lost it
    // Skip initial render when projectData is 'not-found' (handled by loadProjectById)
    // Also skip if we're still loading (project ID just changed)
    if (token && params.projectId && projectData === 'not-found' && wasProjectLoadedRef.current) {
      console.warn('⚠️ Project access lost or project deleted. Redirecting to dashboard...');
      toast.error('You no longer have access to this project. Redirecting to dashboard...');
      localStorage.removeItem('activeProjectTab');
      setTimeout(() => {
        navigate('/');
      }, 1500);
    }
  }, [projectData, params.projectId, token, navigate]);

  // Listen for room creation events to refresh project data
  useEffect(() => {
    const handleRoomCreated = () => {
      console.log('🏠 Room created event received, refreshing project data...');
      if (params.projectId && !isUnmountedRef.current) {
        loadProjectById(params.projectId).catch((error: any) => {
          console.error('❌ Failed to reload project after room creation:', error);
        });
      }
    };

    const handleRoomDeleted = () => {
      console.log('🗑️ Room deleted event received, refreshing project data...');
      if (params.projectId && !isUnmountedRef.current) {
        loadProjectById(params.projectId).catch((error: any) => {
          console.error('❌ Failed to reload project after room deletion:', error);
        });
      }
    };

    window.addEventListener('roomCreated', handleRoomCreated);
    window.addEventListener('roomDeleted', handleRoomDeleted);
    
    return () => {
      window.removeEventListener('roomCreated', handleRoomCreated);
      window.removeEventListener('roomDeleted', handleRoomDeleted);
    };
  }, [params.projectId, loadProjectById]);

  // Load rooms data for counting - similar to MapPopup logic



  const handleNameChange = (newName: string) => {
    if (newName !== (projectData as ProjectInterface)?.name) {
      setPendingNameChange(newName);
      setShowNameEditConfirmation(true);
    }
  };

  const confirmNameChange = async () => {
    setProjectForm({ ...projectForm, name: pendingNameChange });
    setShowNameEditConfirmation(false);
    // Update project immediately
    if (token && params.projectId && typeof projectData !== 'string') {
      const newData = { ...projectData, name: pendingNameChange };
      ['phases', 'items', 'sites', 'users', 'id'].forEach((key:string): boolean => delete newData[key as keyof ProjectInterface]);
      await updateProject(token, params.projectId, newData);
      await loadProjectById(params.projectId);
    }
  };

  // Completed tasks plus completed action items, counted by the backend.
  const getCompletedTasksCount = () => {
    if (typeof projectData !== 'object') return 0;
    return projectData.completed_count ?? 0;
  };



  const handleAddTeamMember = async (_userId: string, userData: any) => {
    try {
      console.log('Project: Adding team member', userData);
      console.log('Project: Current projectUsers count before:', projectUsers?.length);
      
      // Use the proper userId parameter and userData
      await addUser(_userId, userData);
      
      // Force a small delay to ensure state updates
      await new Promise(resolve => setTimeout(resolve, 100));
      
      console.log('Project: Current projectUsers count after:', projectUsers?.length);
      // Don't call setShowAddTeamMember(false) here - let the modal handle its own closing
      // Switch to Team tab after successful creation
      handleTabChange('team');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add team member';
      toast.error(errorMessage);
      throw error;
    }
  };

  const handleAddTask = async (taskData: any) => {
    try {
      if (token && params.projectId) {
        await createProjectItem(token, params.projectId, taskData);
        await loadProjectById(params.projectId);
        toast.success('Task added successfully');
        setShowAddTask(false);
        // Switch to Project Plan tab after successful creation
        handleTabChange('project-plan-2');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add task';
      toast.error(errorMessage);
      throw error;
    }
  };

  const handleAddActionItem = async (actionItemData: any) => {
    try {
      if (token && params.projectId) {
        // If no phase_id, automatically create phase
        let updatedActionItemData = { ...actionItemData };
        if (!updatedActionItemData.phase_id && projectData !== 'not-found') {
          // Check if there are phases in the project
          if (!projectData.phases || projectData.phases.length === 0) {
            console.log('No phases found, creating default phase...');
            try {
              const newPhase = await createProjectPhase(token, params.projectId, {
                id: crypto.randomUUID(),
                project_id: params.projectId,
                name: 'Phase1',
                status: 'NOT_STARTED',
              });
              updatedActionItemData.phase_id = newPhase.id;
              console.log('Created default phase:', newPhase);
              // Update project data to include new phase
              await loadProjectById(params.projectId);
            } catch (phaseError) {
              console.error('Failed to create default phase:', phaseError);
              toast.error('Failed to create required phase');
              throw phaseError;
            }
          } else {
            // Use first available phase
            updatedActionItemData.phase_id = projectData.phases[0].id;
          }
        }

        await createProjectActionItems(token, params.projectId, updatedActionItemData);
        await loadProjectActionItemsPage({ page: 1 });
        toast.success('Action item added successfully');
        setShowAddActionItem(false);
        // Switch to Action Items tab after successful creation
        handleTabChange('action-items');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add action item';
      toast.error(errorMessage);
      throw error;
    }
  };

  const handleAddDailyUpdate = async (dailyUpdateData: any) => {
    try {
      if (token && params.projectId) {
        await createProjectDailyUpdates(token, params.projectId, dailyUpdateData);
        await loadProjectDailyUpdatesPage(1);
        toast.success('Daily update added successfully');
        setShowAddDailyUpdate(false);
        // Switch to Daily Project Update tab after successful creation
        handleTabChange('daily-updates');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add daily update';
      toast.error(errorMessage);
      throw error;
    }
  };



  // Show loading state while project is being loaded
  if (projectData === 'not-found' && params.projectId) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#171717] mx-auto mb-4"></div>
          <p className="text-[#555]">Loading project...</p>
        </div>
      </div>
    );
  }

  return (
    <ProjectAccessProvider projectId={projectId}>
    <div className="flex-1 px-6 lg:px-10 py-6">
      {showNewProject && (
        <NewProjectForm 
          onClose={() => setShowNewProject(false)} 
          onProjectCreated={async (projectId: string) => {
            // Set active tab to team for the newly created project
            localStorage.setItem('activeProjectTab', 'team');
            // Navigate to the new project's Team tab
            navigate(`/projects/${projectId}`);
          }}
        />
      )}
      {showAddTeamMember && projectId && (
        <AddUserModal
          isOpen={showAddTeamMember}
          onClose={() => setShowAddTeamMember(false)}
          onAddUser={handleAddTeamMember}
          availableUsers={[]}
          projectUsers={(projectUsers || []).map(u => u.user)}
          roles={roles || []}
          projectId={projectId}
          onEscalationLevelsUpdate={loadEscalationMatrixLevels}
        />
      )}
      {showAddTask && projectId && typeof projectData === 'object' && (
        <AddProjectItemModal
          isOpen={showAddTask}
          onClose={() => setShowAddTask(false)}
          onAddItem={handleAddTask}
          projectData={projectData}
          users={(projectUsers || []).map(u => u.user)}
          onProjectUpdate={() => loadProjectById(params.projectId || '')}
        />
      )}
      {showAddActionItem && projectId && typeof projectData === 'object' && (
        <AddActionItemModal
          isOpen={showAddActionItem}
          onClose={() => setShowAddActionItem(false)}
          onAddItem={handleAddActionItem}
          projectData={projectData}
          users={(projectUsers || []).map(u => u.user)}
        />
      )}
      {showAddDailyUpdate && projectId && typeof projectData === 'object' && (
        <AddDailyUpdateModal
          isOpen={showAddDailyUpdate}
          onClose={() => setShowAddDailyUpdate(false)}
          onAddItem={handleAddDailyUpdate}
          projectData={projectData}
        />
      )}

      <div className="max-w-[1280px] mx-auto mb-8">
        <div className="flex items-center justify-between mb-6">
          {isFeatureEnabled('GLOBAL_SEARCH') && (
            <div className="relative flex-1 max-w-3xl">
              <input
                type="text"
                placeholder="Search anything or issue"
                className="w-full h-10 pl-4 pr-10 text-[14px] leading-[20px] font-[system-ui,-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,sans-serif] text-[#444] bg-[#fafafa] border border-[#eaeaea] rounded-lg focus:outline-none focus:border-[#888] focus:ring-0"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-[#aaa]" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          )}
          <div className="flex items-center gap-4 mr-4 ml-auto">
            <CreateDropdown 
              onSelectProject={() => setShowNewProject(true)}
              onSelectTeamMember={() => setShowAddTeamMember(true)}
              onSelectTask={() => setShowAddTask(true)}
              onSelectActionItem={() => setShowAddActionItem(true)}
              onSelectDailyUpdate={() => setShowAddDailyUpdate(true)}
            />
            <NotificationButton />
            <UserInfo />
          </div>
        </div>
        {/* Project Header with Stats */}
        <div className="mb-6 pt-2 pb-6">
          <div className="flex flex-col lg:flex-row lg:items-start gap-6 lg:gap-12">
            {/* Left side - Project Info */}
            <div className="flex-1">
              <div>
              <input
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setProjectForm({ ...projectForm, name: e.target.value }) }}
                  className="text-[28px] font-semibold leading-[36px] text-[#171717] border-none bg-transparent p-0 m-0 focus:outline-none w-full tracking-[-0.02em]"
                  onBlur={(e) => handleNameChange(e.target.value)}
                  value={projectData !== 'not-found' ? projectForm.name : 'Project'}
                  placeholder="Project Name"
                />
              </div>
              
              {/* Location */}
              {typeof projectData === 'object' && projectData?.sites && projectData.sites.length > 0 && (
                <div className="flex items-center text-[#888] py-2 transition-colors duration-200 hover:text-[#444]">
                  <svg className="w-4 h-4 mr-1.5 transition-colors duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="font-[system-ui,-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,sans-serif] text-[13px] font-[400] text-[#444] transition-colors duration-200">
                    {getFullAddress(projectData.sites.find((site: any) => site.is_default) || projectData.sites[0])}
                  </span>
                </div>
              )}
              
              {/* First line of Project Description */}
              {typeof projectData === 'object' && projectData?.description && (
                <div>
                  <p className="font-[system-ui,-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,sans-serif] text-[13px] font-[400] leading-[1.3] text-[#444] max-w-2xl">
                    {projectData.description.split('\n')[0].split('.')[0]}{projectData.description.includes('.') ? '.' : ''}
                  </p>
                </div>
              )}
            </div>
            
            {/* Right side - Stats Cards */}
            <div className="flex-shrink-0 overflow-x-auto">
              <div className="flex gap-2 sm:gap-3 lg:gap-4">
            {/* Start Date Card */}
            {typeof projectData === 'object' && projectData?.start_date && (
              <div
                onClick={() => handleTabChange('description')}
                className="bg-white border border-[#eaeaea] rounded-xl p-3 sm:p-4 hover:border-[#ccc] transition-all cursor-pointer min-w-[100px] sm:min-w-[120px] lg:w-36 flex-shrink-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
              >
                <p className="text-[11px] font-medium text-[#888] uppercase tracking-wider mb-1.5">Started</p>
                <p className="text-[15px] font-semibold text-[#171717] tabular-nums">
                    {new Date(projectData.start_date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                </p>
              </div>
            )}

            {/* Team Card */}
            <div
              onClick={() => handleTabChange('team')}
              className="bg-white border border-[#eaeaea] rounded-xl p-3 sm:p-4 hover:border-[#ccc] transition-all cursor-pointer min-w-[100px] sm:min-w-[120px] lg:w-36 flex-shrink-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
            >
                <p className="text-[11px] font-medium text-[#888] uppercase tracking-wider mb-1.5">Team</p>
                <p className="text-[15px] font-semibold text-[#171717]">
                  {typeof projectData === 'object' ? (projectData.users?.length || 0) : 0}
                  <span className="text-[13px] font-normal text-[#888] ml-1">
                    member{(typeof projectData === 'object' ? (projectData.users?.length || 0) : 0) !== 1 ? 's' : ''}
                  </span>
                </p>
            </div>

            {/* Tasks Card */}
            <div
              onClick={() => handleTabChange('project-plan-2')}
              className="bg-white border border-[#eaeaea] rounded-xl p-3 sm:p-4 hover:border-[#ccc] transition-all cursor-pointer min-w-[100px] sm:min-w-[120px] lg:w-36 flex-shrink-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
            >
                <p className="text-[11px] font-medium text-[#888] uppercase tracking-wider mb-1.5">Tasks</p>
                <p className="text-[15px] font-semibold text-[#171717]">
                  {typeof projectData === 'object' ? (projectData.task_count ?? 0) : 0}
                  <span className="text-[13px] font-normal text-[#888] ml-1">
                    item{(typeof projectData === 'object' ? (projectData.task_count ?? 0) : 0) !== 1 ? 's' : ''}
                  </span>
                </p>
            </div>

            {/* Rooms Card */}
            <div
              onClick={() => handleTabChange('locations')}
              className="bg-white border border-[#eaeaea] rounded-xl p-3 sm:p-4 hover:border-[#ccc] transition-all cursor-pointer min-w-[100px] sm:min-w-[120px] lg:w-36 flex-shrink-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
            >
                <p className="text-[11px] font-medium text-[#888] uppercase tracking-wider mb-1.5">Rooms</p>
                <p className="text-[15px] font-semibold text-[#171717]">
                  {typeof projectData === 'object' ? (projectData.rooms?.length || 0) : 0}
                  <span className="text-[13px] font-normal text-[#888] ml-1">
                    room{(typeof projectData === 'object' ? (projectData.rooms?.length || 0) : 0) !== 1 ? 's' : ''}
                  </span>
                </p>
            </div>

            {/* Completed Card */}
            <div
              onClick={() => handleTabChange('project-plan-2')}
              className="bg-white border border-[#eaeaea] rounded-xl p-3 sm:p-4 hover:border-[#ccc] transition-all cursor-pointer min-w-[100px] sm:min-w-[120px] lg:w-36 flex-shrink-0 shadow-[0_1px_3px_rgba(0,0,0,0.04)]"
            >
                <p className="text-[11px] font-medium text-[#888] uppercase tracking-wider mb-1.5">Done</p>
                <p className="text-[15px] font-semibold text-[#171717]">
                  {getCompletedTasksCount()}
                  <span className="text-[13px] font-normal text-[#888] ml-1">
                    task{getCompletedTasksCount() !== 1 ? 's' : ''}
                  </span>
                </p>
            </div>

              </div>
            </div>
          </div>
        </div>
        
        <div className="flex mb-4 w-full border-b border-[#eaeaea] mt-4" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
          <div className="flex gap-6">
            {TABS.filter(tab => {
              // Hide tabs marked as hidden
              if (tab.hidden) return false;

              // Hide Assets tab if feature is not enabled
              if (tab.value === 'assets' && !isFeatureEnabled('ASSETS_TAB')) {
                return false;
              }

              // Hide Warranties tab if feature is not enabled
              if (tab.value === 'warranties' && !isFeatureEnabled('WARRANTIES_TAB')) {
                return false;
              }

              // Hide Download Log tab unless the flag is on and the user is the owner or a system-admin
              if (tab.value === 'download-logs' && (!DOWNLOAD_LOGS_ENABLED || !canViewDownloadLogs(downloadLogsAccess, isProjectOwner))) {
                return false;
              }

              return true;
            }).map((tab) => (
              <button
                disabled={tab.disabled}
                key={tab.value}
                onClick={() => handleTabChange(tab.value)}
                className={`group relative disabled:text-[#aaa] disabled:cursor-not-allowed cursor-pointer bg-transparent py-2.5 text-[13px] leading-[20px] transition-colors duration-150 ${activeTab === tab.value ? 'text-[#171717] font-medium' : 'text-[#888] hover:text-[#444]'}`}
              >
                <div className="relative inline-flex items-center gap-2">
                  {tab.label}
                  {tab.devBadge && isProdEnv && (
                    <span className="text-[10px] uppercase text-[#888] border border-[#eaeaea] rounded px-1 py-[1px] font-normal">
                      dev
                    </span>
                  )}
                  <div
                    className={`absolute left-0 right-0 h-[2px] rounded-full ${
                      activeTab === tab.value
                        ? 'bg-[#171717]'
                        : 'bg-transparent group-hover:bg-[#ddd]'
                    }`}
                    style={{ bottom: '-11.5px' }}
                  />
                </div>
              </button>
            ))}
          </div>
        </div>
        {projectData === 'not-found' && (
          <div className="min-h-screen flex items-center justify-center">
            <LoadingSpinner
              text="Loading project details..."
              className="text-center"
            />
          </div>
        )}

        {typeof projectData === 'object' && activeTab === 'project-plan-2' && (
          <TabProjectPlan
            projectId={projectData.id}
            token={token}
            projectData={projectData}
            tokenParsed={tokenParsed}
            onProjectUpdate={() => loadProjectById(params.projectId || '')}
            highlightedItemId={highlightedItemId}
            initialRiskFilter={location.state?.riskFilter}
          />
        )}

        {typeof projectData === 'object' && activeTab === 'action-items' && (
          <TabActionItems
            projectId={projectData.id}
            token={token}
            projectData={projectData}
            tokenParsed={tokenParsed}
            onProjectUpdate={() => loadProjectById(params.projectId || '')}
            actionItems={projectActionItems[projectData.id || ''] || []}
            highlightedItemId={highlightedItemId}
          />
        )}

        {typeof projectData === 'object' && activeTab === 'sow' && (
          <TabScopeOfWork
            projectId={projectData.id}
            projectData={projectData}
            onProjectUpdate={() => loadProjectById(params.projectId || '')}
            projectUsers={projectUsers}
          />
        )}

        {typeof projectData === 'object' && activeTab === 'daily-updates' && (
          <TabDailyUpdates
            projectId={projectData.id}
            token={token}
            projectData={projectData}
            tokenParsed={tokenParsed}
            onProjectUpdate={() => loadProjectById(params.projectId || '')}
            dailyUpdates={projectDailyUpdates[projectData.id || ''] || []}
            highlightedItemId={highlightedItemId}
          />
        )}

        {typeof projectData === 'object' && activeTab === 'meeting-notes' && (
          <TabMeetingNotes
            projectId={projectData.id}
            token={token}
            projectData={projectData}
            tokenParsed={tokenParsed}
            onProjectUpdate={() => loadProjectById(params.projectId || '')}
            highlightedItemId={highlightedItemId}
            projectUsers={projectUsers}
          />
        )}

        {typeof projectData === 'object' && activeTab === 'team' && (
          <div className="relative">
            <ProjectTeam 
              projectData={typeof projectData === 'string' ? 'not-found' : { ...projectData, id: projectData.id || '' }}
              onProjectUpdate={() => loadProjectById(params.projectId || '')}
            />
            <div className="mt-8">
              <EscalationRules />
            </div>
          </div>
        )}

        {typeof projectData === 'object' && activeTab === 'material-tracker' && (
          <ProjectMaterialTracker 
            projectId={projectData.id || ''}
          />
        )}

        {typeof projectData === 'object' && activeTab === 'assets' && isFeatureEnabled('ASSETS_TAB') && (
          <TabAssets
            key={`assets-${assetsRefreshKey}`} // Force remount when returning from Rack page
            projectId={projectData.id || ''}
            token={token}
          />
        )}

        {typeof projectData === 'object' && activeTab === 'documents' && (
          <TabDocuments
            projectId={projectData.id}
            token={token}
            onNavigateToTab={setActiveTab}
          />
        )}

        {typeof projectData === 'object' && activeTab === 'warranties' && isFeatureEnabled('WARRANTIES_TAB') && (
          <Warranty />
        )}

        {typeof projectData === 'object' && activeTab === 'locations' && (
          <ProjectLocations 
            projectId={projectData.id || ''} 
            projectData={projectData}
            onProjectUpdate={() => loadProjectById(params.projectId || '')}
          />
        )}

        {typeof projectData === 'object' && activeTab === 'download-logs' && (
          <TabDownloadLogs
            projectId={projectData.id || ''}
            isOwner={isProjectOwner}
          />
        )}

        {typeof projectData === 'object' && activeTab === 'settings' && (
          <ProjectDescription
            projectId={projectData.id || ''}
            projectData={projectData}
            onProjectUpdate={() => loadProjectById(params.projectId || '')}
            projectUsers={projectUsers}
          />
        )}
      </div>

      {/* Project Name Edit Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={showNameEditConfirmation}
        onClose={() => {
          setShowNameEditConfirmation(false);
          setPendingNameChange('');
          // Reset form to original name
          if (typeof projectData === 'object') {
            setProjectForm({ ...projectForm, name: projectData.name || '' });
          }
        }}
        onConfirm={confirmNameChange}
        title="Confirm Project Name Change"
        message={`Are you sure you want to change the project name from "${(projectData as ProjectInterface)?.name}" to "${pendingNameChange}"?`}
        confirmText="Update Name"
        cancelText="Cancel"
        confirmButtonClass="bg-[#171717] hover:bg-[#333]"
        warningItems={[
          "This will update the project name for all team members",
          "The change will be visible in all project lists and reports",
          "This action cannot be easily undone"
        ]}
      />
    </div>
    </ProjectAccessProvider>
  );
};

// Archived projects are served by their own view. Deciding here, before any
// hook of ActiveProject mounts, keeps its data-loading effects (which hit
// endpoints that reject inactive projects) from ever running.
export const Project = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();

  if (searchParams.get('archived') === 'true' && projectId) {
    return <ArchivedProjectDetail projectId={projectId} />;
  }

  return <ActiveProject />;
};
